/**
 * Ordered pickup-location editor for pickup tasks.
 *
 * Each stop is either a Vari warehouse (from the registry dropdown) or a custom
 * address typed into the Azure-Maps autocomplete — the same "warehouse OR custom
 * address" pattern as the bid mileage calculator. The list is reorderable
 * (array order = pickup sequence) and rows can be added/removed. Stores a
 * snapshot (name + address + lat/lng) on each entry so the crew can map it.
 */
import { useState } from 'react';
import { ChevronDown, ChevronUp, MapPin, Plus, X } from 'lucide-react';
import type { PickupLocation } from '@vari-crew/schemas';
import { useOriginLocations } from '@/api/locations';
import { AddressAutocomplete } from '@/components/ui/AddressAutocomplete';
import { Button } from '@/components/ui/Button';
import { Label } from '@/components/ui/Input';
import { cn } from '@/lib/cn';

const CUSTOM = '__custom__';
const SELECT_CLS = 'w-full h-9 rounded-md border border-line bg-transparent px-2 text-sm min-w-0';

/** One-line address summary for a stored pickup stop. */
export function pickupAddressLine(loc: PickupLocation): string {
  const cityState = [loc.city, loc.state].filter(Boolean).join(', ');
  return [loc.line1, cityState, loc.postal_code].filter(Boolean).join(' · ');
}

export function PickupLocationsEditor({
  value,
  onChange,
}: {
  value: PickupLocation[];
  onChange: (next: PickupLocation[]) => void;
}) {
  const { data } = useOriginLocations();
  const warehouses = data?.data ?? [];

  function update(i: number, loc: PickupLocation | null) {
    if (!loc) return;
    onChange(value.map((v, idx) => (idx === i ? loc : v)));
  }
  function remove(i: number) {
    onChange(value.filter((_, idx) => idx !== i));
  }
  function move(i: number, dir: -1 | 1) {
    const j = i + dir;
    if (j < 0 || j >= value.length) return;
    const next = [...value];
    [next[i], next[j]] = [next[j]!, next[i]!];
    onChange(next);
  }
  function add() {
    // Append a blank custom row the supervisor then fills in.
    onChange([...value, { warehouse_id: null, name: '', line1: null, line2: null, city: null, state: null, postal_code: null, latitude: null, longitude: null }]);
  }

  return (
    <div>
      <Label>Pickup locations</Label>
      <p className="text-[11px] text-ink-faint mb-1.5">
        Where the crew picks up. Add a warehouse or a custom address; drag order is the pickup
        sequence. The crew sees these as tappable, mappable addresses.
      </p>
      {value.length > 0 && (
        <ul className="space-y-2 mb-2">
          {value.map((loc, i) => (
            <li key={i} className="flex items-start gap-1.5">
              <div className="flex flex-col pt-0.5">
                <button
                  type="button"
                  onClick={() => move(i, -1)}
                  disabled={i === 0}
                  aria-label="Move up"
                  className="text-ink-faint hover:text-ink disabled:opacity-30"
                >
                  <ChevronUp className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => move(i, 1)}
                  disabled={i === value.length - 1}
                  aria-label="Move down"
                  className="text-ink-faint hover:text-ink disabled:opacity-30"
                >
                  <ChevronDown className="w-4 h-4" />
                </button>
              </div>
              <PickupLocationRow
                value={loc}
                warehouses={warehouses}
                onChange={(loc) => update(i, loc)}
              />
              <button
                type="button"
                onClick={() => remove(i)}
                aria-label="Remove pickup location"
                className="mt-1.5 text-ink-faint hover:text-status-blocked-fg"
              >
                <X className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <Button type="button" variant="outline" size="sm" onClick={add}>
        <Plus className="w-3.5 h-3.5" />
        Add pickup location
      </Button>
    </div>
  );
}

type Warehouse = NonNullable<ReturnType<typeof useOriginLocations>['data']>['data'][number];

function PickupLocationRow({
  value,
  warehouses,
  onChange,
}: {
  value: PickupLocation;
  warehouses: Warehouse[];
  onChange: (loc: PickupLocation) => void;
}) {
  const matchedId = value.warehouse_id && warehouses.some((w) => w.id === value.warehouse_id)
    ? value.warehouse_id
    : value.name || value.latitude != null
      ? CUSTOM
      : '';
  const [customOpen, setCustomOpen] = useState(matchedId === CUSTOM);
  const [customText, setCustomText] = useState(matchedId === CUSTOM ? value.name : '');

  return (
    <div className="flex-1 min-w-0 space-y-1">
      <select
        value={matchedId}
        onChange={(e) => {
          const v = e.target.value;
          if (v === CUSTOM) {
            setCustomOpen(true);
            return;
          }
          setCustomOpen(false);
          const w = warehouses.find((x) => x.id === v);
          if (w) {
            onChange({
              warehouse_id: w.id,
              name: w.name,
              line1: w.line1,
              line2: null,
              city: w.city,
              state: w.state,
              postal_code: w.postal_code,
              latitude: w.latitude,
              longitude: w.longitude,
            });
          }
        }}
        className={SELECT_CLS}
      >
        <option value="">Choose a warehouse…</option>
        {warehouses.map((w) => (
          <option key={w.id} value={w.id}>
            {w.name}
          </option>
        ))}
        <option value={CUSTOM}>Custom address…</option>
      </select>
      {customOpen && (
        <AddressAutocomplete
          value={customText}
          onChange={setCustomText}
          onSelect={(s) => {
            if (s.latitude == null || s.longitude == null) return;
            const name = s.poi_name || s.label;
            setCustomText(name);
            onChange({
              warehouse_id: null,
              name,
              line1: s.line1,
              line2: null,
              city: s.city,
              state: s.state,
              postal_code: s.postal_code,
              latitude: s.latitude,
              longitude: s.longitude,
            });
          }}
          placeholder="Search an address…"
          className="h-9"
        />
      )}
      {/* Confirmation line for a chosen stop. */}
      {(value.name || value.line1) && (
        <div className={cn('flex items-start gap-1 text-[11px] text-ink-faint')}>
          <MapPin className="w-3 h-3 mt-0.5 shrink-0" />
          <span className="min-w-0">
            <span className="font-medium text-ink-dim">{value.name}</span>
            {pickupAddressLine(value) && <span className="block truncate">{pickupAddressLine(value)}</span>}
          </span>
        </div>
      )}
    </div>
  );
}
